import dayjs from "@punchup/utils/dayjs";

import type { AUTOMATIONS_PURCHASE_CATEGORY } from "./automations-entitlement";
import {
  AUTOMATIONS_MOGUL_TIER,
  AUTOMATIONS_TIER_CREDITS,
  isAutomationsBillingInterval,
  isAutomationsTier,
  type AutomationsBillingInterval,
  type AutomationsPlanKey,
  type AutomationsTier,
} from "./automations-plans";

/** The metadata contract every Automations checkout and plan change writes; `credit_allotment` is the MONTHLY allotment on every interval. */
export type AutomationsSubscriptionMetadata = {
  purchase_category: typeof AUTOMATIONS_PURCHASE_CATEGORY;
  artist_id: string;
  user_id: string;
  tier: AutomationsTier;
  interval: AutomationsBillingInterval;
  credit_allotment: string;
  overage_enabled: "true" | "false";
  overage_cap_cents: string;
  /** Stamped by an upgrade for the invoice.paid webhook to deposit; not a plan fact. */
  upgrade_top_up?: string;
  upgrade_by_user_id?: string;
};

export type AutomationsPlan =
  | { kind: "tier"; tier: AutomationsTier }
  | { kind: "mogul"; allotment: number | null }
  | { kind: "unknown" };

export type AutomationsPlanTier =
  | AutomationsTier
  | typeof AUTOMATIONS_MOGUL_TIER;

export interface PlanItemLike {
  price?: {
    recurring?: { interval?: string; usage_type?: string } | null;
  } | null;
  current_period_start?: number | null;
  current_period_end?: number | null;
}

type MetadataLike = Record<string, string> | null | undefined;

export interface AutomationsPlanFacts {
  plan: AutomationsPlan;
  interval: AutomationsBillingInterval | null;
  /** One month's credit pool; null when `credit_allotment` is absent, zero or garbage. */
  monthlyAllotment: number | null;
}

/** The plan facts a metadata-only source holds (invoice snapshot, schedule phase). */
export function automationsPlanFromMetadata(
  metadata: MetadataLike
): AutomationsPlanFacts {
  const parsed = parseInt(metadata?.credit_allotment ?? "", 10);
  const monthlyAllotment = parsed > 0 ? parsed : null;
  const tier = metadata?.tier;
  const interval = metadata?.interval;
  return {
    plan: isAutomationsTier(tier)
      ? { kind: "tier", tier }
      : tier === AUTOMATIONS_MOGUL_TIER
        ? { kind: "mogul", allotment: monthlyAllotment }
        : { kind: "unknown" },
    interval: isAutomationsBillingInterval(interval) ? interval : null,
    monthlyAllotment,
  };
}

const INTERVAL_OF_PRICE: Record<string, AutomationsBillingInterval> = {
  month: "monthly",
  year: "annual",
};

export type AutomationsSubscriptionPlanFacts<I> = AutomationsPlanFacts & {
  baseItem: I | null;
  period: { start: string; end: string } | null;
};

/** A subscription's stamped plan snapshot: tier and allotment from metadata, interval from the base price (the metered overage item is never the base). A server read wants `chargedPlanOf`, which prefers the plan the base price charges. */
export function automationsPlanSnapshotOf<I extends PlanItemLike>(sub: {
  metadata: MetadataLike;
  items: { data: I[] };
}): AutomationsSubscriptionPlanFacts<I> {
  const facts = automationsPlanFromMetadata(sub.metadata);
  const baseItem =
    sub.items.data.find(
      (item) => item.price?.recurring?.usage_type !== "metered"
    ) ?? null;
  const { current_period_start: start, current_period_end: end } =
    baseItem ?? {};
  return {
    ...facts,
    interval:
      INTERVAL_OF_PRICE[baseItem?.price?.recurring?.interval ?? ""] ?? null,
    baseItem,
    period:
      start && end
        ? {
            start: dayjs.unix(start).toISOString(),
            end: dayjs.unix(end).toISOString(),
          }
        : null,
  };
}

export function planTierOf(plan: AutomationsPlan): AutomationsPlanTier | null {
  if (plan.kind === "tier") return plan.tier;
  return plan.kind === "mogul" ? AUTOMATIONS_MOGUL_TIER : null;
}

/** The plan keys a writer stamps; `interval` is still written because the annual-refill cron searches Stripe by it. */
export function automationsPlanMetadata({
  tier,
  interval,
}: AutomationsPlanKey) {
  return {
    tier,
    interval,
    credit_allotment: String(AUTOMATIONS_TIER_CREDITS[tier]),
  } satisfies Partial<AutomationsSubscriptionMetadata>;
}
